import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Check, ExternalLink, PauseCircle, PlayCircle, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";

import { EmptyState } from "@/components/shared/EmptyState";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { PageHeader } from "@/components/shared/PageHeader";
import { StatusBadge, type StatusBadgeVariant } from "@/components/shared/StatusBadge";
import { TableCard } from "@/components/shared/TableCard";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { serverMessage } from "@/lib/apiError";
import { formatDisplayDate } from "@/lib/dateFormat";
import {
  approveBankTransfer,
  dismissBankTransfer,
  listBankTransfersForReview,
  reconcileBankTransfer,
  type BankTransferReviewItem,
  type BankTransferVerification,
} from "@/services/platformBankTransfersService";
import { pauseRequest, unpauseRequest } from "@/services/platformRequestPauseService";

/**
 * The human half of bank-transfer verification.
 *
 * Everything the automatic pass could not settle lands here: a masked
 * destination account, an unreadable photo, a missing reference, a gateway
 * outage. Auto-verification only ever establishes that a receipt MATCHES —
 * this is where someone establishes that the money actually ARRIVED, against
 * the real bank statement.
 *
 * Deliberately shows the model's raw transcription NEXT TO our own per-field
 * verdict. A reviewer needs to see what was read and what we concluded from
 * it separately: "the amount does not match" and "the amount was misread" look
 * identical in a single verdict and call for opposite actions.
 *
 * There is no reject button. Approving, reconciling and pausing are the only
 * actions, because rejecting a transfer is a money decision with no safe
 * automatic consequence — the request is already held, and doing nothing is
 * the correct default. Refunds of off-platform transfers are manual by design.
 *
 * The three actions answer three different questions and are deliberately not
 * collapsed into one:
 *   approve   — "treat this receipt as paid" (only for a transfer the
 *               automatic pass could not settle; releases the held request).
 *   reconcile — "I have seen the money on the statement". The claim
 *               auto-verification cannot make, and the only thing that takes a
 *               row off this queue. The backend refuses it until the checkout
 *               is paid, so the button stays disabled until then.
 *   pause     — "stop the work while I find out". For the window between a
 *               receipt matching and the funds actually landing: the request
 *               is already moving, and the professional must not finish (and
 *               earn a payout line) on money that never arrived.
 */

/** The review queue's cache key. Every action on a row invalidates it. */
const QUEUE_QUERY_KEY = ["platform", "bank-transfers"] as const;

const VERIFICATION_VARIANT: Record<BankTransferVerification, StatusBadgeVariant> = {
  verified: "success",
  mismatch: "danger",
  needs_review: "warning",
  unverified: "softDanger",
  pending: "info",
};

function formatAmount(amountMinor: number, currency: string): string {
  return new Intl.NumberFormat("es-UY", {
    style: "currency",
    currency: currency || "UYU",
    minimumFractionDigits: 2,
  }).format(amountMinor / 100);
}

/**
 * A single extracted field beside what OUR check made of it.
 *
 * Three states, not two. A masked destination account that matched by its
 * trailing digits is the single most common reason a receipt reaches this
 * queue, and drawing it as a red cross labelled "does not match" tells the
 * reviewer the opposite of the truth. "partial" says what actually happened:
 * as much as could be compared did match.
 */
type FieldOutcome = "match" | "partial" | "miss";

function FieldRow({
  label,
  read,
  outcome,
}: {
  label: string;
  read: string | null | undefined;
  outcome?: FieldOutcome;
}) {
  const { t } = useTranslation(undefined, { keyPrefix: "platformBankTransfers" });
  return (
    <div className="flex items-baseline justify-between gap-3 py-1">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="flex items-center gap-1.5">
        <span className="font-mono text-xs text-foreground">
          {read?.trim() ? read : t("fieldAbsent")}
        </span>
        {outcome === "match" ? (
          <Check className="h-3.5 w-3.5 text-green-600" aria-label={t("fieldMatched")} />
        ) : outcome === "partial" ? (
          <AlertTriangle
            className="h-3.5 w-3.5 text-amber-500"
            aria-label={t("fieldPartiallyMatched")}
          />
        ) : outcome === "miss" ? (
          <X className="h-3.5 w-3.5 text-red-500" aria-label={t("fieldNotMatched")} />
        ) : null}
      </span>
    </div>
  );
}

/** boolean -> outcome, for the fields whose check really is yes/no. */
function outcomeOf(ok: boolean | undefined): FieldOutcome | undefined {
  return ok === undefined ? undefined : ok ? "match" : "miss";
}


/**
 * Everything a reviewer can DO to one transfer.
 *
 * Split out of ReviewRow because the row is a presentation of what was read
 * and concluded, while this owns the three actions, their in-flight state and
 * the two free-text fields that accompany them. Keeping them together made one
 * component responsible for both, and the note/reason state leaked into markup
 * that has no use for it.
 */
function TransferActions({ item }: { item: BankTransferReviewItem }): JSX.Element {
  const { t } = useTranslation(undefined, { keyPrefix: "platformBankTransfers" });
  const queryClient = useQueryClient();
  const [note, setNote] = useState("");
  const [pauseReason, setPauseReason] = useState("");

  /**
   * The money side is settled. This is exactly the backend's precondition for
   * reconciling and nothing more: it says the payment is confirmed, NOT that
   * the receipt verified cleanly — an admin approval settles a transfer whose
   * automatic verdict was never `verified`.
   */
  const isSettled = item.status === "paid";
  /**
   * The payment is still live — not settled, not abandoned. Narrower than
   * "not paid": a `failed` or `cancelled` checkout stays in this
   * queue (the backend filters only on submitted-and-unreconciled), and
   * approveManually short-circuits solely on `paid` — so without this an
   * operator could promote a cancelled checkout to paid and fire the
   * request-promotion event off a payment that was abandoned.
   */
  const isPaymentPending = item.status === "pending";
  /** Abandoned: no payment decision is left to make on it. */
  const isTerminal = item.status === "cancelled" || item.status === "failed";

  /**
   * Every action here changes the request as well as the queue: approving
   * releases the held request, reconciling retires the row, pausing stops the
   * work. So all three invalidate the same set. The admin detail page keys on
   * "notarial-request" for BOTH channels (RequestsDetailRoute routes every
   * admin request to PlatformNotarialRequestDetailPage), so one key covers
   * notary and accountant alike.
   */
  const invalidateAffectedViews = (): Promise<unknown> =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: QUEUE_QUERY_KEY }),
      queryClient.invalidateQueries({ queryKey: ["platform", "requests"] }),
      queryClient.invalidateQueries({
        queryKey: ["platform", "notarial-request", item.request?.id],
      }),
    ]);

  const approve = useMutation({
    mutationFn: () => approveBankTransfer(item.checkoutId, note.trim() || undefined),
    onSuccess: async () => {
      toast.success(t("approved"));
      setNote("");
      await invalidateAffectedViews();
    },
    onError: (error) => toast.error(serverMessage(error) ?? t("approveFailed")),
  });

  const reconcile = useMutation({
    mutationFn: () => reconcileBankTransfer(item.checkoutId, note.trim() || undefined),
    onSuccess: async () => {
      toast.success(t("reconciled"));
      setNote("");
      await invalidateAffectedViews();
    },
    onError: (error) => toast.error(serverMessage(error) ?? t("reconcileFailed")),
  });

  const dismiss = useMutation({
    mutationFn: () => dismissBankTransfer(item.checkoutId, note.trim()),
    onSuccess: async () => {
      toast.success(t("dismissed"));
      setNote("");
      await invalidateAffectedViews();
    },
    onError: (error) => toast.error(serverMessage(error) ?? t("dismissFailed")),
  });

  /**
   * Pause and unpause are one mutation, not two.
   *
   * They are mutually exclusive on the same row, so a single pending flag
   * disables the control correctly while either is in flight — two mutations
   * would need the flags combined at every use anyway.
   */
  const setPaused = useMutation({
    mutationFn: (paused: boolean) => {
      const request = item.request;
      // Unreachable: the control only renders when a request is linked. Throwing
      // rather than silently resolving keeps a future regression loud.
      if (!request) throw new Error("No request linked to this transfer");
      return paused
        ? pauseRequest(request.channel, request.id, pauseReason.trim() || undefined)
        : unpauseRequest(request.channel, request.id);
    },
    onSuccess: async (_data, paused) => {
      toast.success(paused ? t("paused") : t("unpaused"));
      setPauseReason("");
      await invalidateAffectedViews();
    },
    onError: (error, paused) =>
      toast.error(serverMessage(error) ?? (paused ? t("pauseFailed") : t("unpauseFailed"))),
  });

  return (
    <>
      <div className="mt-4">
        {/*
          One note for both money actions, cleared once either is recorded, so
          a note written for an approval is never resent as the reconciliation
          note. The pause reason stays separate: it is shown to the
          professional, while this is written for us.
        */}
        <label
          htmlFor={`note-${item.checkoutId}`}
          className="block text-xs font-medium text-foreground"
        >
          {t("noteLabel")}
        </label>
        <input
          id={`note-${item.checkoutId}`}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={t("notePlaceholder")}
          maxLength={500}
          className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
        />
        {/*
          Gated on the checkout's own status, not on the verification verdict.
          A live transfer can be approved and a settled one reconciled, so a row
          awaiting a decision always offers exactly one action; a terminal
          checkout offers neither, and says so. Gating on
          `verification === "verified"` instead would read almost identically
          today — persist() writes 'paid' in the same save that writes
          'verified' — but it describes the wrong precondition.
        */}
        <Button
          className="mt-3 w-full"
          disabled={approve.isPending || !isPaymentPending}
          onClick={() => approve.mutate()}
        >
          {isSettled
            ? t("alreadyConfirmed")
            : isPaymentPending
              ? t("approve")
              : t("checkoutClosed")}
        </Button>
        <p className="mt-2 text-[11px] text-muted-foreground">
          {isPaymentPending
            ? t("approveHint")
            : isSettled
              ? t("approveDoneHint")
              : t("checkoutClosedHint")}
        </p>

        {/*
          Reconcile is gated on the money side being settled, exactly as the
          backend gates it: reconciling a checkout that is not yet paid would
          retire it from this queue without anyone having approved it. Disabled
          with the reason stated, rather than enabled into a 400.
        */}
        <Button
          variant="outline"
          className="mt-4 w-full"
          disabled={reconcile.isPending || !isSettled}
          onClick={() => reconcile.mutate()}
        >
          {t("reconcile")}
        </Button>
        <p className="mt-2 text-[11px] text-muted-foreground">
          {isSettled ? t("reconcileHint") : t("reconcileBlockedHint")}
        </p>

        {/*
          Only for an abandoned checkout, and only with a note. This is the one
          action that retires a row WITHOUT answering whether the money
          arrived, so the note is the entire record of why it left the queue —
          and offering it on a live or settled checkout would turn it into a
          way to clear the queue without deciding anything.
        */}
        {isTerminal ? (
          <>
            <Button
              variant="ghost"
              className="mt-4 w-full text-muted-foreground"
              disabled={dismiss.isPending || note.trim().length < 3}
              onClick={() => dismiss.mutate()}
            >
              {t("dismiss")}
            </Button>
            <p className="mt-2 text-[11px] text-muted-foreground">
              {note.trim().length < 3 ? t("dismissNeedsNote") : t("dismissHint")}
            </p>
          </>
        ) : null}
      </div>

      {/*
        Pausing acts on the REQUEST, not on the checkout, so it only appears
        once a request exists. A stranded paid checkout has nothing to pause —
        what it needs is a request, which is a different problem.
      */}
      {item.request ? (
        <div className="mt-6 rounded-lg border border-border bg-background px-3 py-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t("workTitle")}
          </h3>
          {item.request.paused ? (
            <>
              <p className="mt-2 text-xs text-foreground">{t("workPausedDescription")}</p>
              <Button
                variant="outline"
                className="mt-3 w-full"
                disabled={setPaused.isPending}
                onClick={() => setPaused.mutate(false)}
              >
                <PlayCircle className="mr-1.5 h-4 w-4" aria-hidden />
                {t("unpause")}
              </Button>
              <p className="mt-2 text-[11px] text-muted-foreground">{t("unpauseHint")}</p>
            </>
          ) : (
            <>
              <label
                htmlFor={`pause-reason-${item.checkoutId}`}
                className="mt-2 block text-xs font-medium text-foreground"
              >
                {t("pauseReasonLabel")}
              </label>
              <input
                id={`pause-reason-${item.checkoutId}`}
                value={pauseReason}
                onChange={(e) => setPauseReason(e.target.value)}
                placeholder={t("pauseReasonPlaceholder")}
                maxLength={500}
                className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
              />
              {/*
                A reason is required, not optional. The professional sees a job
                stop accepting actions; the endpoint accepts a blank reason, so
                if this let one through the stop would be unexplained — which
                is the one thing the reason exists to prevent.
              */}
              <Button
                variant="outline"
                className="mt-3 w-full"
                disabled={setPaused.isPending || pauseReason.trim().length === 0}
                onClick={() => setPaused.mutate(true)}
              >
                <PauseCircle className="mr-1.5 h-4 w-4" aria-hidden />
                {t("pause")}
              </Button>
              <p className="mt-2 text-[11px] text-muted-foreground">{t("pauseHint")}</p>
            </>
          )}
        </div>
      ) : null}
    </>
  );
}

function ReviewRow({ item }: { item: BankTransferReviewItem }) {
  const { t } = useTranslation(undefined, { keyPrefix: "platformBankTransfers" });
  const [open, setOpen] = useState(false);
  const extraction = item.detail?.extraction;
  const result = item.detail?.result;
  const verification = item.verification ?? "pending";

  return (
    <>
      <TableRow className={item.paidWithoutRequest ? "bg-red-50/60 dark:bg-red-900/10" : undefined}>
        <TableCell className="align-top">
          <div className="font-mono text-xs text-foreground">{item.reference ?? "—"}</div>
          <div className="mt-0.5 font-mono text-[11px] text-muted-foreground">
            {item.checkoutId.slice(0, 8)}
          </div>
        </TableCell>
        <TableCell className="align-top font-mono tabular-nums">
          {formatAmount(item.amountMinor, item.currency)}
        </TableCell>
        <TableCell className="align-top">
          <div className="flex flex-col items-start gap-1">
            <StatusBadge variant={VERIFICATION_VARIANT[verification]}>
              {t(`verification.${verification}`)}
            </StatusBadge>
            {/*
              Paid with no request is not a verdict — it is a consumer who has
              paid and received nothing. It outranks whatever the verdict says.
            */}
            {item.paidWithoutRequest ? (
              <span className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[10px] font-medium text-red-700 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-300">
                <AlertTriangle className="h-3 w-3" aria-hidden />
                {t("paidWithoutRequest")}
              </span>
            ) : null}
            {/*
              Paused work is visible without expanding the row: a stopped
              request is the state most likely to need a decision today, and
              hiding it behind "show detail" is how it gets left stopped.
            */}
            {item.request?.paused ? (
              <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-300">
                <PauseCircle className="h-3 w-3" aria-hidden />
                {t("workPaused")}
              </span>
            ) : null}
          </div>
        </TableCell>
        <TableCell className="align-top text-xs text-muted-foreground">
          {item.submittedAt ? formatDisplayDate(item.submittedAt) : "—"}
        </TableCell>
        <TableCell className="align-top">
          <div className="flex items-center justify-end gap-2">
            {item.receiptUrl ? (
              <Button asChild variant="outline" size="sm">
                <a href={item.receiptUrl} target="_blank" rel="noopener noreferrer">
                  {t("openReceipt")}
                  <ExternalLink className="ml-1.5 h-3.5 w-3.5" aria-hidden />
                </a>
              </Button>
            ) : null}
            <Button variant="ghost" size="sm" onClick={() => setOpen((v) => !v)}>
              {open ? t("hideDetail") : t("showDetail")}
            </Button>
          </div>
        </TableCell>
      </TableRow>

      {/*
        Rendered whether or not the row is expanded, and hidden with CSS rather
        than unmounted. react-query skips a mutation's onSuccess when the
        component that owns it unmounts, so collapsing a row mid-approval used
        to swallow the toast AND the cache invalidation, leaving the operator
        looking at a row that had already been acted on. Hiding also keeps a
        half-typed note when a row is collapsed and reopened.
      */}
      <TableRow className={open ? undefined : "hidden"} aria-hidden={!open}>
        <TableCell colSpan={5} className="bg-muted/40">
            <div className="grid gap-6 py-2 md:grid-cols-2">
              <div>
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {t("extractionTitle")}
                </h3>
                {extraction ? (
                  <div className="rounded-lg border border-border bg-background px-3 py-2">
                    <FieldRow
                      label={t("fieldAmount")}
                      read={extraction.amount}
                      outcome={outcomeOf(result?.amountMatched)}
                    />
                    <FieldRow
                      label={t("fieldAccount")}
                      read={extraction.destinationAccount}
                      outcome={
                        result === undefined
                          ? undefined
                          : result.accountMatch === "exact"
                            ? "match"
                            : result.accountMatch === "suffix"
                              ? "partial"
                              : "miss"
                      }
                    />
                    <FieldRow
                      label={t("fieldName")}
                      read={extraction.destinationName}
                      outcome={outcomeOf(result?.nameMatched)}
                    />
                    <FieldRow
                      label={t("fieldReference")}
                      read={extraction.reference}
                      outcome={outcomeOf(result?.referenceMatched)}
                    />
                    <FieldRow label={t("fieldOperation")} read={extraction.operationNumber} />
                    <FieldRow label={t("fieldDate")} read={extraction.transferDate} />
                    <FieldRow label={t("fieldOriginBank")} read={extraction.originBank} />
                    <FieldRow
                      label={t("fieldConfidence")}
                      read={
                        typeof extraction.confidence === "number"
                          ? extraction.confidence.toFixed(2)
                          : null
                      }
                    />
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    {item.detail?.error
                      ? t("checkNeverRan", { error: item.detail.error })
                      : t("noExtraction")}
                  </p>
                )}
              </div>

              <div>
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {t("reasonsTitle")}
                </h3>
                {Array.isArray(result?.reasons) && result.reasons.length > 0 ? (
                  <ul className="list-inside list-disc space-y-1 text-xs text-foreground">
                    {result.reasons.map((r) => (
                      <li key={r}>{r}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs text-muted-foreground">{t("noReasons")}</p>
                )}

                {item.detail?.manualApproval ? (
                  <p className="mt-3 rounded-lg bg-background px-3 py-2 text-xs text-muted-foreground">
                    {t("alreadyApprovedBy", {
                      by: item.detail.manualApproval.by,
                      at: formatDisplayDate(item.detail.manualApproval.at),
                    })}
                    {item.detail.manualApproval.note
                      ? ` — ${item.detail.manualApproval.note}`
                      : ""}
                  </p>
                ) : null}

                <TransferActions item={item} />
              </div>
            </div>
          </TableCell>
      </TableRow>
    </>
  );
}

export function PlatformBankTransfersPage(): JSX.Element {
  const { t } = useTranslation(undefined, { keyPrefix: "platformBankTransfers" });

  const listQuery = useQuery({
    queryKey: QUEUE_QUERY_KEY,
    queryFn: listBankTransfersForReview,
  });

  return (
    <div className="space-y-6">
      <PageHeader title={t("title")} description={t("description")} />

      {listQuery.isPending ? (
        <div className="py-16 text-center">
          <LoadingLogo />
        </div>
      ) : listQuery.isError ? (
        <EmptyState title={t("loadError")} />
      ) : (listQuery.data?.length ?? 0) === 0 ? (
        <EmptyState title={t("emptyTitle")} message={t("emptyDescription")} />
      ) : (
        <TableCard>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("colReference")}</TableHead>
                <TableHead>{t("colAmount")}</TableHead>
                <TableHead>{t("colVerification")}</TableHead>
                <TableHead>{t("colSubmitted")}</TableHead>
                <TableHead className="text-right">{t("colActions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {listQuery.data?.map((item) => (
                <ReviewRow key={item.checkoutId} item={item} />
              ))}
            </TableBody>
          </Table>
        </TableCard>
      )}
    </div>
  );
}
